/**
 * Whether notes are stuck to the page.
 *
 * A note only existed as a card in a list under the clause, which is the right
 * place for the record and the wrong place for the reading: by the time you
 * reach it you have scrolled past the sentence it is about. The post-it puts
 * the words beside the words.
 *
 * It is a preference and not a session state, because the alternative is that
 * a person turns the notes off to read a long clause cleanly, reloads, and
 * finds them back. A setting that resets is a setting nobody uses twice.
 *
 * Storage is optional at every step. `localStorage` throws on access in
 * Safari's private mode rather than returning null, and a reading pane that
 * fails to render because a preference could not be read would be a far worse
 * bug than the one this fixes.
 */

const KEY = 'tmea.notes-on-page';

/** The browser's store, or nothing at all, without ever throwing. */
function store(): Storage | null {
  try {
    return globalThis.localStorage ?? null;
  } catch {
    return null;
  }
}

/**
 * On by default.
 *
 * The affordance is new, and a feature that ships switched off is a feature
 * nobody discovers. Anyone who wants the clean reading column turns it off
 * once and it stays off.
 */
export function readNotesOnPage(from: Storage | null = store()): boolean {
  try {
    const raw = from?.getItem(KEY);
    if (raw === 'off') return false;
    // Anything else — 'on', absent, or junk left by an older build — is on.
    return true;
  } catch {
    return true;
  }
}

export function writeNotesOnPage(on: boolean, to: Storage | null = store()): void {
  try {
    to?.setItem(KEY, on ? 'on' : 'off');
  } catch {
    // A preference that cannot be remembered is still a preference that works
    // for this session. Nothing about the reader depends on this succeeding.
  }
}
